import type { Messages } from './en';

/**
 * Fase E (paquete taroko): Garganta de Taroko y playa de Qixingtan.
 * Las líneas de práctica de inglés se quedan en inglés a propósito.
 * En las frases de los personajes se mantiene la marca “English practice:” tal cual:
 * el juego la usa para saber qué frase leer en voz alta en inglés.
 */
export const esTaroko: Pick<Messages, 'taroko'> = {
  taroko: {
    mapNames: { hualien: 'Garganta de Taroko y Qixingtan' },
    zones: {
      shrine: 'Santuario de la Eterna Primavera',
      buluowan: 'Terraza de Buluowan',
      shakadang: 'Sendero de Shakadang',
      qingshui: 'Acantilados de Qingshui',
      village: 'Pueblo de Qixingtan',
      visitor: 'Centro de visitantes de Taroko',
    },
    objects: {
      tk_visitor: 'Centro de visitantes',
      tk_shuttleTaroko: 'Parada del Tourist Shuttle (Taroko)',
      tk_shuttleBeach: 'Parada del Tourist Shuttle (Qixingtan)',
      tk_cimu: 'Pabellón del puente Cimu',
      tk_rockSign: 'Señal de desprendimientos',
      tk_swallows: 'Cartel de la Gruta de las Golondrinas',
      tk_shrine: 'Santuario de la Eterna Primavera',
      tk_bell: 'Campanario',
      tk_buluowan: 'Cartel de Buluowan',
      tk_weave: 'Telar truku',
      tk_monkeys: 'Aviso de monos',
      tk_mochi: 'Puesto de mochi de mijo',
      tk_shakadang: 'Cartel del sendero de Shakadang',
      tk_bikeRent: 'Alquiler de bicis',
      tk_skip: 'Piedras planas',
      tk_sunrise: 'Puente del Amanecer',
      tk_stargaze: 'Mirador de estrellas',
      tk_museum: 'Museo del bonito',
      tk_sausage: 'Puesto de salchichas de pez volador',
    },
    npc: {
      tk_guide: 'Guía turística',
      tk_rangerB: 'Guarda del sendero',
      tk_weaver: 'Tejedora truku',
      tk_mochiAuntie: 'Tía truku',
      tk_fisher: 'Pescador',
      tk_kid1: 'Niño que hace cabrillas',
      tk_kid2: 'Niña que hace cabrillas',
      tk_cyclist: 'Ciclista',
      tk_photographer: 'Fotógrafa',
      tk_bonito: 'Guía del museo',
      tk_stargazer: 'Guía de estrellas',
    },
    items: {},

    common: {
      firstStar: '⭐ ¡Nuevo descubrimiento!',
      notHere: 'Mmm, no pasa nada.',
    },
    visitor: {
      text: 'Centro de visitantes del Parque Nacional de Taroko. Una gran maqueta en 3D muestra la garganta: 19 km de acantilados de mármol excavados por el río Liwu.',
      ask: '🗺️ Preguntar el camino (en inglés)',
      q: '¿Cómo se pregunta el camino con educación?',
      // English practice
      a: ['Excuse me, how do I get to the Shakadang Trail?', 'Trail where? Tell me.', 'Shakadang now, go go go!'],
      right: '“Walk west along the river and cross the red bridge. The stairs go down to the trail!”',
      wrong: 'El voluntario sonríe: «Prueba de una forma más educada: empieza con “Excuse me…”».',
    },
    cimu: {
      text: 'Puente Cimu (慈母橋): «el puente de la madre cariñosa». Una madre esperaba aquí cada día a su hijo, que construía la carretera de montaña. El puente se llamó así por su amor.',
      listen: '🌉 Mirar el puente rojo',
      looked: 'Dos leones de mármol blanco vigilan el puente. Abajo, el río baja blanco y rápido.',
      // English practice
      phrase: 'Thank you, Mom! I love you.',
    },
    rock: {
      text: 'Un triángulo amarillo con piedras cayendo. Pueden caer rocas de los acantilados en cualquier momento.',
      q: '¿Qué significa esta señal?',
      // English practice
      a: ['Watch out for falling rocks!', 'Free rocks — take one!', 'A rock concert tonight.'],
      right: '¡Exacto! No te quites el casco y no te pares debajo de los acantilados.',
      wrong: 'Vuelve a mirar: caen piedras por la ladera…',
    },
    swallows: {
      text: 'Gruta de las Golondrinas (燕子口): el río excavó cientos de agujeritos en el mármol, ¡y las golondrinas hacen sus nidos dentro!',
      count: '🐦 Contar las golondrinas',
      q: (birds: string) => `${birds}\n\n¿Cuántas golondrinas ves?`,
      right: '¡Bien contado! Las golondrinas vuelven aquí cada primavera.',
      wrong: 'Vuelve a contar, despacio: uno, dos, tres…',
      // English practice: number words.
      numbers: ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'],
    },
    shrine: {
      text: 'El Santuario de la Eterna Primavera (長春祠) recuerda a los 226 trabajadores que murieron construyendo la carretera que cruza la isla por el centro. El manantial que hay debajo nunca deja de manar: por eso es «eterno».',
      bow: '🙏 Hacer una reverencia en silencio',
      bowed: 'Te quedas quieto un momento. La cascada ruge bajo el tejado rojo.',
      // English practice
      phrase: '“Eternal” means “forever”.',
    },
    bell: {
      text: 'Un campanario en lo alto de unas escaleras empinadas. ¡Desde aquí se ve toda la garganta!',
      ring: '🔔 Tocar la campana',
      rang: 'Dong… dong… El sonido rebota de acantilado en acantilado.',
      // English practice
      phrase: 'I rang the bell. (ring → rang → rung)',
    },
    buluowan: {
      text: 'Buluowan (布洛灣) es una terraza donde vivían y cultivaban familias truku. En lengua truku, Buluowan significa «eco».',
      shout: '📣 Gritar “Hello!” hacia la garganta',
      // English practice
      hello: 'Hello!',
      echo: '“Hello… hello… hello…” ¡Las montañas te contestan!',
    },
    weave: {
      text: 'Un telar de cintura truku. El dibujo de rombos rojos se llama «los ojos de los antepasados»: significa que la familia está protegida y a salvo.',
      try: '🧵 Aprender la palabra (en inglés)',
      q: '¿Qué está haciendo?',
      // English practice
      a: ['She is weaving cloth.', 'She is eating cloth.', 'She is sleeping on cloth.'],
      right: '¡Sí! “Weave” es tejer: teje hilos rojos y blancos para hacer tela.',
      wrong: 'Fíjate en cómo mueve los hilos con las manos…',
    },
    monkeys: {
      text: 'En estos acantilados viven macacos de Formosa (台灣獼猴). Son salvajes… ¡y les interesan muchísimo tus tentempiés!',
      q: 'Un mono mira tu mochi. ¿Qué haces?',
      // English practice
      a: ['Don’t feed the monkeys. I keep my food in my bag.', 'I give it all my snacks!', 'I try to touch it.'],
      right: '¡Correcto! La comida de las personas pone enfermos a los monos salvajes y los vuelve unos tragones.',
      wrong: 'Mmm… los monos pueden morder, y nuestra comida les sienta mal. ¡Prueba otra vez!',
    },
    // English practice: food order.
    mochi: { vendor: 'Millet mochi, made with our Truku millet! Peanut or plain?', ok: 'Two peanut ones, please. How much are they?', bad: ['Mochi. Give.', 'I want everything, no money.'], done: 'Blandito y elástico, con un sabor a mijo que recuerda a los frutos secos. La tía te saluda: «¡Que disfrutes del sendero!».', price: 30 },
    shakadang: {
      text: 'Sendero de Shakadang (砂卡礑步道): un camino excavado en el acantilado sobre un arroyo de agua cristalina. ¡“Skadang” es la palabra truku para muela!',
      look: '💧 Mirar el agua',
      q: '¿De qué color es el agua?',
      // English practice
      a: ['It’s blue-green and very clear.', 'It’s brown and muddy.', 'It’s bright red.'],
      right: '¡Sí! Se ven todos los guijarros de mármol blanco del fondo.',
      wrong: 'Fíjate bien en el arroyo…',
    },
    bike: {
      text: 'Alquiler de bicis: el carril bici recorre toda la costa de Qixingtan.',
      ride: '🚲 Alquilar una bici (en inglés)',
      q: '¿Cómo lo pides?',
      // English practice
      a: ['I’d like to rent a bike for one hour, please.', 'Bike. One. Now.', 'Give me your bike.'],
      right: 'Pedaleas junto a la costa con el viento en el pelo. ¡Montañas a la izquierda y el Pacífico a la derecha!',
      wrong: 'El dueño se ríe: «¿Me lo puedes decir con más educación?».',
      price: 20,
      poor: 'No tienes suficientes monedas.',
    },
    skip: {
      text: 'Guijarros planos y lisos: perfectos para hacer cabrillas sobre las olas.',
      throw: '🥏 Lanzar una piedra',
      again: '🥏 Otra vez',
      result: (n: number, words: string) => (n === 0 ? '¡Plof! Se ha hundido a la primera.' : `Salta, salta… ¡ha saltado ${n} ${n === 1 ? 'vez' : 'veces'}! ${words}`),
      great: '¡Increíble! Los niños te aplauden.',
    },
    sunrise: {
      text: 'El Puente del Amanecer (曙光橋). Hualien es uno de los primeros sitios de Taiwán donde se ve salir el sol sobre el Pacífico.',
      early: '🌅 Hacer una foto del amanecer',
      photo: '📸 ¡Clic! El sol sale del océano, naranja y dorado. ¡Qué fotaza!',
      late: 'El sol ya está alto. ¡Vuelve antes de las 7 de la mañana para la foto del amanecer!',
      // English practice
      phrase: 'The sun rises in the east.',
    },
    stars: {
      text: 'Un mirador de estrellas. Qixingtan (七星潭) significa «Lago de las Siete Estrellas», por las siete estrellas del Carro (la Osa Mayor).',
      look: '✨ Mirar el cielo',
      day: 'Es de día y las estrellas están escondidas. ¡Vuelve cuando oscurezca!',
      q: '¿Cuántas estrellas tiene el Carro?',
      // English practice
      a: ['Seven.', 'Three.', 'Twenty.'],
      right: '¡Siete! Sigue las dos estrellas del final del «cazo» y encontrarás la Estrella Polar.',
      wrong: 'Vuelve a contar las estrellas con forma de cazo…',
    },
    museum: {
      text: 'Museo del Bonito de Qixingtan (柴魚博物館). Qixingtan era un pueblo de pescadores: ahumaban y secaban el bonito hasta que quedaba duro como la madera.',
      learn: '🐟 Aprender una curiosidad',
      q: '¿Por qué en chino al bonito seco lo llaman «pez leña» (柴魚)?',
      // English practice
      a: ['Because dried bonito is as hard as wood.', 'Because it lives in trees.', 'Because it is cooked on a campfire only.'],
      right: '¡Correcto! Luego se ralla en láminas finitas que «bailan» encima del tofu caliente.',
      wrong: 'Pista: da unos golpecitos a un bonito seco… ¡toc, toc!',
    },
    // English practice: food order.
    sausage: { vendor: 'Flying-fish sausage! A Hualien speciality. With garlic?', ok: 'Yes, please. One sausage with garlic.', bad: ['Sausage! Fast!', 'Fish. Garlic. Mine.'], done: 'Ahumada, jugosa y un poco salada, como el mar. ¡Perfecta después de un baño!', price: 35 },

    talk: {
      ranger: {
        intro: '¡Bienvenido a Taroko! Antes de entrar en los senderos de la garganta, una pregunta rápida de seguridad.',
        q: '¿Qué tienes que llevar puesto en los senderos de la garganta?',
        // English practice
        a: ['A helmet.', 'Flip-flops.', 'Sunglasses only.'],
        right: '¡Muy bien! Pide uno en la caseta de cascos: es gratis.',
        wrong: 'Piensa en las piedras que caen…',
      },
      tk_guide: [
        'Please follow me and stay together! This way, everyone! (English practice)',
        'En lengua truku, Taroko significa «magnífico y precioso».',
        'Nuestro autobús sale a las tres. ¡No lleguéis tarde!',
      ],
      tk_rangerB: [
        'Por favor, no os paréis debajo de los acantilados. Seguid andando.',
        '“Watch out!” significa «¡cuidado, que viene algo!».',
        'Hace millones de años, este mármol era el fondo del mar.',
      ],
      tk_weaver: [
        'Mi abuela me enseñó a tejer. El rojo es la vida y el valor.',
        'English practice: “What are you making?” — “I’m making a bag.”',
        'Antiguamente, las chicas truku aprendían a tejer antes de casarse.',
      ],
      tk_mochiAuntie: [
        'El mijo crece bien en la montaña. Con él hacemos vino y mochi.',
        'English practice: “How much is it?” — “Thirty dollars.”',
      ],
      tk_fisher: [
        'Salgo antes del amanecer. ¡Por la mañana los peces tienen hambre!',
        'English practice: “Did you catch anything?” — “Yes, three fish!”',
        'Aquí el mar se hace hondo muy deprisa. No te bañes: solo mójate los pies.',
      ],
      tk_kid1: [
        '¡Mi récord son siete saltos! ¿Puedes superarlo?',
        'Busca una piedra plana y tírala baja y rápida. ¡Así!',
      ],
      tk_kid2: [
        'English practice: “Your turn!” — “Okay, here I go!”',
        'También apilamos piedras. ¡La mía es una torre de diez!',
      ],
      tk_cyclist: [
        '“On your left!”: eso es lo que se dice en inglés cuando adelantas a alguien.',
        'El carril bici llega hasta la ciudad. ¡Veinte kilómetros!',
      ],
      tk_photographer: [
        'Los acantilados de Qingshui miden mil metros. ¡No me caben en la cámara!',
        'English practice: “Could you take a photo of us, please?”',
        'Ven al amanecer: el mar se vuelve rosa y dorado.',
      ],
      tk_bonito: [
        'English practice: “Would you like to try some?” — “Yes, please!”',
        'Las virutas de bonito se ahúman durante muchos días. ¿Lo hueles?',
      ],
      tk_stargazer: [
        '¡Mira arriba! Esa tan brillante no es una estrella: es un planeta.',
        'English practice: “Can you see the Big Dipper?” — “Yes, I can!”',
        'Lejos de las luces de la ciudad, aquí se ve la Vía Láctea.',
      ],
    },
  },
};
